import { useEffect, useState } from 'react';
import { useVault } from '../lib/vault';
import { api } from '../lib/api';
import type { AuditEvent } from '@shared/types';
import { estimateBits } from '../lib/crypto';
import { REMEMBER_LABEL, rememberedUntil, type RememberChoice } from '../lib/keystore';
import { Confirm, ErrorNote, useAction } from '../components/ui';
import { WelcomeText, useWelcome } from '../components/Welcome';

const ACTION_LABEL: Record<string, string> = {
  login: 'Anmeldung',
  'vault-key.create': 'Passphrase gesetzt',
  'vault-key.rewrap': 'Passphrase geändert',
  'vault-key.reset': 'Schlüssel zurückgesetzt',
  'item.create': 'Eintrag angelegt',
  'item.update': 'Eintrag gespeichert',
  'item.update-meta': 'Stammdaten geändert',
  'item.rotate': 'Passwort geändert',
  'item.delete': 'Eintrag gelöscht',
  'item.grant': 'Freigabe erteilt',
  'item.revoke': 'Freigabe entzogen',
  'item.regrant': 'Zugänge wiederhergestellt',
  'user.update': 'Konto geändert',
  'user.delete': 'Konto gelöscht',
  'offboarding.start': 'Offboarding gestartet',
  'offboarding.check': 'Checkliste',
  'offboarding.complete': 'Offboarding abgeschlossen',
};

function EntsperrtBleiben(): JSX.Element {
  const { rememberChoice, setRememberChoice } = useVault();
  const [bis, setBis] = useState<Date | null>(null);
  const { busy, error, run } = useAction();

  useEffect(() => {
    void rememberedUntil().then(setBis);
  }, [rememberChoice]);

  return (
    <div className="card">
      <div className="card-title">
        <h2>Entsperrt bleiben</h2>
      </div>
      <p className="small muted">
        Gilt nur für dieses Gerät und diesen Browser. Beim Sperren und beim Abmelden wird der
        gemerkte Schlüssel sofort verworfen.
      </p>

      <ErrorNote error={error} />

      <div className="field" style={{ maxWidth: 360, marginTop: 12 }}>
        <label htmlFor="remember">Nach dem Entsperren merken für</label>
        <select
          id="remember"
          value={rememberChoice}
          disabled={busy}
          onChange={(e) => void run(() => setRememberChoice(e.target.value as RememberChoice))}
        >
          {(Object.keys(REMEMBER_LABEL) as RememberChoice[]).map((k) => (
            <option key={k} value={k}>
              {REMEMBER_LABEL[k]}
            </option>
          ))}
        </select>
      </div>

      {bis ? (
        <p className="small muted">
          Aktuell gemerkt bis <strong>{bis.toLocaleString('de-DE')}</strong>.
        </p>
      ) : null}

      <div className="note" style={{ marginTop: 12 }}>
        Bitte benutzt das Entsperren auf Dauer nur, wenn es euer eigener Rechner ist. Bitte denkt
        daran, dass hier vertrauliche Informationen stehen.
      </div>
    </div>
  );
}

function ChangePassphrase(): JSX.Element {
  const { changeMasterPassphrase } = useVault();
  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [repeat, setRepeat] = useState('');
  const [done, setDone] = useState(false);
  const { busy, error, setError, run } = useAction();

  const ready = oldPass && newPass.length >= 12 && newPass === repeat;

  return (
    <div className="card">
      <div className="card-title">
        <h2>Master-Passphrase ändern</h2>
      </div>
      <p className="small muted">
        Das Schlüsselpaar bleibt dabei erhalten – alle deine Freigaben gelten weiter.
      </p>

      <ErrorNote error={error} />
      {done ? <div className="note note-ok">Passphrase geändert.</div> : null}

      <form
        style={{ marginTop: 12, maxWidth: 420 }}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!ready) {
            setError('Neue Passphrase mindestens 12 Zeichen, beide Eingaben identisch.');
            return;
          }
          const ok = await run(async () => {
            await changeMasterPassphrase(oldPass, newPass);
            return true;
          });
          if (ok) {
            setDone(true);
            setOldPass('');
            setNewPass('');
            setRepeat('');
          }
        }}
      >
        <div className="field">
          <label htmlFor="old">Aktuelle Passphrase</label>
          <input id="old" type="password" value={oldPass} onChange={(e) => setOldPass(e.target.value)} autoComplete="off" data-1p-ignore="true" data-lpignore="true" data-bwignore="true" />
        </div>
        <div className="field">
          <label htmlFor="new">Neue Passphrase</label>
          <input id="new" type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} autoComplete="off" data-1p-ignore="true" data-lpignore="true" data-bwignore="true" />
          {newPass ? <p className="small muted" style={{ margin: '6px 0 0' }}>{estimateBits(newPass)} Bit geschätzt</p> : null}
        </div>
        <div className="field">
          <label htmlFor="rep">Wiederholen</label>
          <input id="rep" type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)} autoComplete="off" data-1p-ignore="true" data-lpignore="true" data-bwignore="true" />
        </div>
        <button className="btn-primary" type="submit" disabled={busy || !ready}>
          {busy ? 'Wird geändert …' : 'Ändern'}
        </button>
      </form>
    </div>
  );
}

function AuditLog(): JSX.Element {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .audit()
      .then(setEvents)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  return (
    <div className="card">
      <div className="card-title">
        <h2>Protokoll</h2>
        <span className="small muted">letzte 300 Ereignisse</span>
      </div>
      <ErrorNote error={error} />
      {events.length === 0 && !error ? (
        <div className="empty">Noch nichts passiert.</div>
      ) : (
        <table>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td className="small muted" style={{ whiteSpace: 'nowrap' }}>
                  {new Date(e.at).toLocaleString('de-DE')}
                </td>
                <td className="small">{ACTION_LABEL[e.action] ?? e.action}</td>
                <td className="small muted col-optional">{e.actorEmail}</td>
                <td className="small">{e.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function SettingsPage(): JSX.Element {
  const { me, can, lock, resetKey, logout } = useVault();
  const [confirmReset, setConfirmReset] = useState(false);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Einstellungen</h1>
          <p>Angemeldet als {me?.email}.</p>
        </div>
        <div className="row-tight">
          <button onClick={lock}>Sperren</button>
          <button onClick={() => void logout()}>Abmelden</button>
        </div>
      </div>

      <div className="card">
        <div className="card-title">
          <h2>Wie LOCKER schützt</h2>
        </div>
        <div className="stack small">
          <p style={{ margin: 0 }}>
            <strong>Zwei getrennte Schlösser.</strong> Der Google-Login entscheidet, wer überhaupt
            hereinkommt. Deine Master-Passphrase entscheidet, was du lesen kannst. Wer nur eines von
            beidem hat, sieht keine Passwörter.
          </p>
          <p style={{ margin: 0 }}>
            <strong>Verschlüsselt wird im Browser.</strong> Der Server bekommt Passwörter und Notizen
            nur als Geheimtext zu sehen. Selbst mit vollem Zugriff auf die Datenbank ließe sich daraus
            nichts lesen.
          </p>
          <p style={{ margin: 0 }}>
            <strong>Vertragsdaten sind offen.</strong> Dienst, Preis, Laufzeit und Zuständigkeit sieht
            das ganze Team – anders ließe sich keine Kostenübersicht bauen. Login-Name, Passwort,
            2FA-Schlüssel und Notizen sieht nur, wer freigeschaltet ist.
          </p>
          <p style={{ margin: 0 }}>
            <strong>Automatische Sperre.</strong> Nach 15 Minuten ohne Aktivität wird LOCKER
            gesperrt. Auch ein Neuladen der Seite verlangt die Passphrase erneut – sie wird nirgends
            zwischengespeichert.
          </p>
          <p style={{ margin: 0 }}>
            <strong>Was LOCKER nicht kann.</strong> Passwörter beim Anbieter selbst ändern. Wenn
            jemand geht, entzieht das Offboarding zwar sofort den Zugriff hier – die Passwörter bei
            Slack, Adobe und Co. muss trotzdem ein Mensch neu setzen. Dafür ist die Checkliste da.
          </p>
        </div>
      </div>

      <EntsperrtBleiben />

      <ChangePassphrase />

      <div className="card">
        <div className="card-title">
          <h2>Schlüssel zurücksetzen</h2>
        </div>
        <p className="small muted">
          Nur nötig, wenn du die Passphrase vergessen hast. Alle deine Freigaben werden gelöscht und
          müssen dir neu erteilt werden.
        </p>
        <button className="btn-danger" style={{ marginTop: 10 }} onClick={() => setConfirmReset(true)}>
          Schlüssel zurücksetzen
        </button>
      </div>

      {can('manageTeam') ? <WelcomeEditor /> : null}

      {can('viewAudit') ? <AuditLog /> : null}

      {confirmReset ? (
        <Confirm
          danger
          title="Schlüssel wirklich zurücksetzen?"
          confirmLabel="Zurücksetzen"
          message={
            <>
              <p>
                Dein Schlüsselpaar wird verworfen. Alle Einträge, die dir freigegeben waren, verlierst
                du – ein Admin oder eine Kollegin muss sie dir zurückgeben.
              </p>
              <p className="muted">Einträge, die nur dir freigegeben waren, sind danach für niemanden mehr lesbar.</p>
            </>
          }
          onConfirm={async () => {
            await resetKey();
          }}
          onClose={() => setConfirmReset(false)}
        />
      ) : null}
    </>
  );
}


/**
 * Der Begrüßungstext, den Mitglieder auf ihrer Startseite sehen.
 *
 * Mit Vorschau, weil sonst niemand nachsehen kann, wie er wirkt: Admins
 * bekommen auf ihrer eigenen Startseite ja die Kostenübersicht.
 */
function WelcomeEditor(): JSX.Element {
  const willkommen = useWelcome();
  const { busy, error, run } = useAction();
  const [entwurf, setEntwurf] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState(false);

  const text = entwurf ?? willkommen.text;

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-title">
        <h2>Begrüßung für das Team</h2>
        <span className="small muted">
          {willkommen.isDefault ? 'derzeit der vorgeschlagene Text' : 'selbst geschrieben'}
        </span>
      </div>
      <p className="small muted" style={{ marginTop: 0 }}>
        Diesen Text sehen alle, die keine Kostenübersicht haben, auf ihrer Startseite.
        Leerzeile trennt Absätze, <code>#</code> macht eine Zwischenüberschrift, <code>-</code> einen
        Aufzählungspunkt, <code>**Text**</code> setzt fett. Leerst du das Feld, gilt wieder der
        vorgeschlagene Text.
      </p>

      <ErrorNote error={error} />
      {gespeichert ? <div className="note note-ok">Gespeichert.</div> : null}

      <textarea
        value={text}
        rows={14}
        spellCheck
        onChange={(e) => {
          setEntwurf(e.target.value);
          setGespeichert(false);
        }}
        style={{ width: '100%', fontFamily: 'inherit', lineHeight: 1.55 }}
      />

      <div className="row-tight" style={{ marginTop: 10 }}>
        <button
          className="btn-primary"
          disabled={busy || entwurf === null}
          onClick={async () => {
            await run(async () => {
              await api.setWelcome(text);
              willkommen.reload();
              setEntwurf(null);
              setGespeichert(true);
            });
          }}
        >
          Speichern
        </button>
        {entwurf !== null ? (
          <button className="btn-quiet" onClick={() => setEntwurf(null)}>
            Änderungen verwerfen
          </button>
        ) : null}
      </div>

      <div className="note" style={{ marginTop: 14 }}>
        <div className="small muted" style={{ marginBottom: 8 }}>So sieht es aus:</div>
        <WelcomeText text={text} />
      </div>
    </div>
  );
}
